/**
 * InspectionPunchList — standalone clone of the IssuesPanel ("Punch List")
 * from HomeownerHub. Lives on the Inspection Report page so HomeHQ can be
 * locked out for client/advisor without affecting due diligence.
 *
 * Intentionally does NOT share helpers with HomeownerHub — fully inlined so
 * future HomeHQ changes / removal cannot break this view.
 */

import { Box, Button, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Clock,
  ListFilter,
  PieChart as PieChartIcon,
  Search,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';

import { ScrollHintList } from '@/components/dashboard/scoring';
import { CloseButton, SortCaret } from '@/components/ds';
import { RFQCompileIndicator } from '@/components/home/RFQCompileIndicator';
import { ReviewAndShareCompileDialog } from '@/components/home/rfqs/ReviewAndShareCompileDialog';
import { ReviewAndShareRFQDialog } from '@/components/home/rfqs/ReviewAndShareRFQDialog';
import { RfqGenerationDialog } from '@/components/home/rfqs/RfqGenerationDialog';
import { RfqHistoryDialog } from '@/components/home/rfqs/RfqHistoryDialog';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { refreshActivityForProperty } from '@/hooks/activity';
import { JobState } from '@/models/DocumentProcessingJob';
import { Issue } from '@/models/Issue';
import { RequestForQuotation } from '@/models/RequestForQuotation';
import { getDocumentProcessingJob } from '@/services/api/document-processing-jobs';
import { requestRFQForIssue } from '@/services/api/issues';
import { getRequestForQuote, searchRequestForQuoteByIssue } from '@/services/api/rfqs';
import { useTranslationPrefix } from '@/translations/i18n';

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function retrieveOrCreateRFQ(issueId: string): Promise<RequestForQuotation> {
  const existing = await searchRequestForQuoteByIssue(issueId);
  if (existing.length > 0) return existing[0];

  const job = await requestRFQForIssue(issueId);
  const maxAttempts = 120;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    await sleep(2000);
    const current = await getDocumentProcessingJob(job.id);
    if (current.state === JobState.failed) {
      throw new Error(current.errorReason || 'RFQ generation failed');
    }
    if (current.state === JobState.completed) break;
  }

  const afterGen = await searchRequestForQuoteByIssue(issueId);
  if (afterGen.length === 0) {
    throw new Error('RFQ not available after generation completed');
  }
  return afterGen[0];
}

const C = {
  bg: 'var(--tx-fill-2)',
  bg2: 'var(--tx-glass-panel)',
  bg3: 'var(--tx-glass-panel)',
  bg4: '#383838',
  canvas: '#bbb7af',
  lime: '#c0ff02',
  teal: '#5AE9FF',
  yellow: '#FFF981',
  orange: '#FF9A4D',
  pink: '#DB5282',
  green: '#4CAF79',
  terra: '#C4622D',
  text: 'var(--tx-text)',
  muted: 'var(--tx-faint)',
  border: 'var(--tx-line)',
};

const card = (extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: C.bg2,
  backgroundImage: 'var(--tx-surface-light)',
  boxShadow: 'var(--tx-shadow)',
  border: 0,
  borderRadius: '24px',
  padding: '20px',
  ...extra,
});

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
}

const ISSUES_SEV_W: Record<string, number> = {
  Critical: 2.5,
  Urgent: 1.5,
  Moderate: 1,
  Low: 0.5,
  Monitor: 0.25,
};
const ISSUES_SEV_COLORS: Record<string, string> = {
  Critical: C.pink,
  Urgent: C.orange,
  Moderate: C.yellow,
  Low: C.teal,
  Monitor: C.muted,
};
type IssuesSortKey = 'urgency' | 'cost' | 'category' | 'name';

function Checkbox({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      style={{
        width: 16,
        height: 16,
        borderRadius: 4,
        border: `1.5px solid ${checked ? C.lime : '#555'}`,
        background: checked ? C.lime : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all .15s',
      }}>
      {checked && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path
            d="M2 5l2 2 4-4"
            stroke="#111"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}

function HeaderCell({
  label,
  active,
  dir,
  onClick,
  align,
  marginLeft,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
  align?: 'left' | 'right';
  marginLeft?: number;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: C.muted,
        fontFamily: "'Manrope',sans-serif",
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: align ?? 'left',
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        marginLeft,
      }}>
      {label}
      <SortCaret active={active} dir={dir} />
    </div>
  );
}

export function InspectionPunchList() {
  const { selectedProperty, propertyIssues } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const queryClient = useQueryClient();
  const allIssues = propertyIssues ?? [];
  const m = useWindowWidth() <= 768;
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<IssuesSortKey>('urgency');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const navigate = useNavigate();
  const { state: routerState } = useLocation();
  const [urgencyFilter, setUrgencyFilter] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState<Set<string>>(new Set());
  // Pre-filter by category when navigated from Top Concerns (run once on mount)
  const routerCategory = routerState?.category as string | undefined;
  useEffect(() => {
    if (routerCategory) setCategoryFilter(new Set([routerCategory]));
  }, [routerCategory]);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [urgencyMenuOpen, setUrgencyMenuOpen] = useState(false);
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const cardWrapRef = useRef<HTMLDivElement>(null);
  const [mobileCardHeight, setMobileCardHeight] = useState<string>('calc(100dvh - 160px)');
  useEffect(() => {
    if (!m) return;
    const measure = () => {
      if (!cardWrapRef.current) return;
      const rect = cardWrapRef.current.getBoundingClientRect();
      setMobileCardHeight(`calc(100dvh - ${Math.round(rect.top) + 10}px)`);
    };
    measure();
    // The card's top offset can shift after this mount — the mobile property
    // selector's height depends on an async property fetch, and the scroll
    // container resets scrollTop in a sibling effect in DashboardLayout — so
    // a single measurement here can race and bake in a stale (too-large) top,
    // leaving a gap below the card. Poll briefly to catch that settle, same
    // as the Dialog/InspectionCarouselOverlay centering pattern.
    const interval = window.setInterval(measure, 50);
    const timeout = window.setTimeout(() => window.clearInterval(interval), 800);
    window.addEventListener('resize', measure);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
      window.removeEventListener('resize', measure);
    };
  }, [m]);
  const [shareProgress, setShareProgress] = useState<{ done: number; total: number } | null>(null);
  const [rfqGenerationIssues, setRfqGenerationIssues] = useState<Issue[]>([]);
  const [pieOpen, setPieOpen] = useState(false);
  const [pieHoverIdx, setPieHoverIdx] = useState<number | null>(null);
  const [pieBlurLeft, setPieBlurLeft] = useState<number | null>(null);
  const [pieContentRect, setPieContentRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [pieCardRect, setPieCardRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  useEffect(() => {
    if (!pieOpen) return undefined;
    const measure = () => {
      const sidebar = document.getElementById('dashboard-left-sidebar');
      setPieBlurLeft(sidebar ? sidebar.getBoundingClientRect().right : 0);
      const main = document.getElementById('dashboard-main-content');
      if (main) {
        const r = main.getBoundingClientRect();
        setPieContentRect({ top: r.top, left: r.left, width: r.width });
      }
      const card = document.getElementById('inspection-punch-list-card');
      if (card) {
        const r = card.getBoundingClientRect();
        setPieCardRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    const sidebar = document.getElementById('dashboard-left-sidebar');
    const main = document.getElementById('dashboard-main-content');
    const card = document.getElementById('inspection-punch-list-card');
    if (sidebar) ro.observe(sidebar);
    if (main) ro.observe(main);
    if (card) ro.observe(card);
    window.addEventListener('resize', measure);
    const transitionTimer = window.setInterval(measure, 50);
    const transitionStop = window.setTimeout(() => window.clearInterval(transitionTimer), 400);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      window.clearInterval(transitionTimer);
      window.clearTimeout(transitionStop);
    };
  }, [pieOpen]);
  useEffect(() => {
    if (!pieOpen) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPieOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [pieOpen]);
  const [reviewRFQ, setReviewRFQ] = useState<RequestForQuotation | null>(null);
  const [reviewCompileRFQs, setReviewCompileRFQs] = useState<RequestForQuotation[] | null>(null);
  const [reviewSuggestedTrades, setReviewSuggestedTrades] = useState<string[]>([]);
  const [rfqHistoryOpen, setRfqHistoryOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const rfqId = searchParams.get('rfqId');
    if (!rfqId || reviewRFQ?.id === rfqId) return;

    let cancelled = false;
    getRequestForQuote(rfqId)
      .then((rfq) => {
        if (cancelled) return;
        if (selectedProperty && rfq.propertyId !== selectedProperty.property.id) return;
        const linkedIssue = rfq.issueId ? allIssues.find((i) => i.id === rfq.issueId) : undefined;
        const trade = linkedIssue?.trade ?? linkedIssue?.componentCategory ?? linkedIssue?.category;
        setReviewSuggestedTrades(trade ? [trade] : []);
        setReviewRFQ(rfq);
      })
      .catch((err) => {
        console.error('Failed to load RFQ from URL', err);
        showAlert("Couldn't load that RFQ.", 'error');
      });

    return () => {
      cancelled = true;
    };
  }, [allIssues, searchParams, reviewRFQ?.id, selectedProperty, showAlert]);

  useEffect(() => {
    if (!reviewRFQ?.issueId || reviewSuggestedTrades.length > 0) return;
    const linkedIssue = allIssues.find((i) => i.id === reviewRFQ.issueId);
    const trade = linkedIssue?.trade ?? linkedIssue?.componentCategory ?? linkedIssue?.category;
    if (trade) setReviewSuggestedTrades([trade]);
  }, [allIssues, reviewRFQ?.issueId, reviewSuggestedTrades.length]);

  useEffect(() => {
    // dev only: opens Review & Adjust with a sample RFQ; production never honours ?reviewMock
    if (!import.meta.env.DEV || searchParams.get('reviewMock') !== '1') return;
    if (reviewRFQ) return;
    setReviewRFQ({
      id: 'mock-rfq-1',
      propertyId: selectedProperty?.property.id ?? 'mock-property',
      issueId: 'mock-issue-1',
      propertyAddress: selectedProperty?.property.address ?? '123 Main Street',
      clientContact: { name: 'Homeowner', phoneNumber: '[Redacted]', email: '[Redacted]' },
      projectTitle: 'Installation of Handrail on Rear Stairs',
      objective:
        'Install a compliant handrail on rear stairs to enhance safety and meet building code requirements.',
      scopeOfWork: [
        'Measure and evaluate stair dimensions',
        'Supply and install a durable, weather-resistant handrail',
        'Ensure compliance with local codes (34-38" height, 1 1/4-2" grip diameter)',
        'Securely anchor handrail and ensure proper alignment',
        'Remove debris and clean the worksite upon completion',
      ],
      timeline: { desiredStartDate: 'ASAP', completionDate: 'Within 1-2 days of project start' },
      nextSteps:
        'Contractors are welcome to review prior inspection reports, perform their own onsite assessment, or order to provide accurate estimates and timelines.',
      submissionRequirements: {
        dueInDays: 7,
        method: 'Email proposal to homeowner@example.com',
        include: [
          'Cost breakdown for materials and labor',
          'Proposed timeline for start and completion',
          'Proof of licensing and insurance (Bond, WC and GL)',
          'Warranty information for materials and workmanship',
        ],
      },
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }, [searchParams, reviewRFQ, selectedProperty]);

  const clearRfqSearchParam = () => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.delete('rfqId');
      return next;
    });
  };

  const issueCost = (i: Issue) => {
    const min = i.costEstimateMin ?? 0;
    const max = i.costEstimateMax ?? min;
    return (min + max) / 2;
  };

  const NEUTRAL_POOL = [
    '#BBB7AF',
    '#8B8680',
    '#D6D2C9',
    '#9C8E7A',
    '#A89E92',
    '#5A5852',
    '#E8E3D8',
  ];
  const categoryBreakdown = useMemo(() => {
    const buckets = new Map<string, { name: string; count: number; totalCost: number }>();
    for (const i of allIssues) {
      if (i.resolved) continue;
      const key = (i.trade ?? i.category) || 'Uncategorized';
      const entry = buckets.get(key) ?? { name: key, count: 0, totalCost: 0 };
      entry.count += 1;
      entry.totalCost += issueCost(i);
      buckets.set(key, entry);
    }
    const sorted = Array.from(buckets.values()).sort((a, b) => b.count - a.count);
    const result: { name: string; count: number; totalCost: number; color: string }[] = [];
    for (let i = 0; i < sorted.length; i += 1) {
      const prev = result[i - 1]?.color;
      const wrap = i === sorted.length - 1 ? result[0]?.color : null;
      let color = NEUTRAL_POOL[i % NEUTRAL_POOL.length];
      for (let off = 0; off < NEUTRAL_POOL.length; off += 1) {
        const candidate = NEUTRAL_POOL[(i + off) % NEUTRAL_POOL.length];
        if (candidate !== prev && candidate !== wrap) {
          color = candidate;
          break;
        }
      }
      result.push({ ...sorted[i], color });
    }
    return result;
  }, [allIssues]);

  const allCategories = useMemo(
    () => Array.from(new Set(allIssues.map((i) => i.trade ?? i.category).filter(Boolean))).sort(),
    [allIssues],
  );

  const filtered = useMemo(() => {
    let result = allIssues.filter((i) => !i.resolved);
    if (urgencyFilter.size > 0) result = result.filter((i) => urgencyFilter.has(i.urgency));
    if (categoryFilter.size > 0)
      result = result.filter((i) => categoryFilter.has(i.trade ?? i.category));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (i) =>
          i.title.toLowerCase().includes(q) || (i.trade ?? i.category).toLowerCase().includes(q),
      );
    }
    const sorted = [...result].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'urgency')
        cmp = (ISSUES_SEV_W[b.urgency] ?? 0) - (ISSUES_SEV_W[a.urgency] ?? 0);
      else if (sortKey === 'cost') cmp = issueCost(b) - issueCost(a);
      else if (sortKey === 'category')
        cmp = (a.trade ?? a.category).localeCompare(b.trade ?? b.category);
      else if (sortKey === 'name') cmp = a.title.localeCompare(b.title);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [allIssues, urgencyFilter, categoryFilter, search, sortKey, sortDir]);

  const urgencyCounts = useMemo(() => {
    const counts: Record<string, number> = {
      Critical: 0,
      Urgent: 0,
      Moderate: 0,
      Low: 0,
      Monitor: 0,
    };
    for (const i of allIssues) {
      if (!i.resolved && counts[i.urgency] !== undefined) counts[i.urgency] += 1;
    }
    return counts;
  }, [allIssues]);

  const SORT_LABELS: Record<IssuesSortKey, string> = {
    urgency: 'By Urgency',
    cost: 'By Cost',
    category: 'By Category',
    name: 'By Name',
  };

  const selectedCost = useMemo(
    () => filtered.filter((i) => selectedIds.has(i.id)).reduce((s, i) => s + issueCost(i), 0),
    [filtered, selectedIds],
  );

  const toggleSort = (key: IssuesSortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const toggleUrgency = (u: string) =>
    setUrgencyFilter((prev) => {
      const next = new Set(prev);
      if (next.has(u)) next.delete(u);
      else next.add(u);
      return next;
    });
  const toggleCategory = (c: string) =>
    setCategoryFilter((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleExpand = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length && filtered.length > 0) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((i) => i.id)));
  };
  const handleShareRFQs = async () => {
    if (shareProgress) return;
    const issues = filtered.filter((i) => selectedIds.has(i.id));
    if (issues.length === 0) return;

    setRfqGenerationIssues(issues);
    setShareProgress({ done: 0, total: issues.length });
    try {
      setReviewSuggestedTrades(
        issues.map((issue) => issue.trade ?? issue.componentCategory ?? issue.category),
      );
      const rfqs: RequestForQuotation[] = [];
      for (let idx = 0; idx < issues.length; idx += 1) {
        const rfq = await retrieveOrCreateRFQ(issues[idx].id);
        rfqs.push(rfq);
        setShareProgress({ done: idx + 1, total: issues.length });
      }
      refreshActivityForProperty(queryClient, propertyId);

      if (rfqs.length === 1) {
        setReviewRFQ(rfqs[0]);
      } else {
        setReviewCompileRFQs(rfqs);
      }
    } catch (err) {
      console.error('Failed to create share link', err);
      showAlert(t('rfq.share_failed'), 'error');
    } finally {
      setShareProgress(null);
      setRfqGenerationIssues([]);
    }
  };

  const fmtTimeline = (i: Issue): string | null => {
    const min = i.projectTimelineMinDays;
    const max = i.projectTimelineMaxDays;
    if (!min && !max) return null;
    const upper = (max ?? min) as number;
    if (upper <= 1) return '≤ 1 Day';
    if (min && max && min !== max) return `${min}–${max} Days`;
    return `${upper} Days`;
  };

  const fmtCost = (i: Issue) => {
    const min = i.costEstimateMin;
    const max = i.costEstimateMax;
    if (!min && !max) return '—';
    if (min && max && min !== max)
      return `$${
        min / 1000 >= 1
          ? `${(min / 1000).toFixed(min % 1000 === 0 ? 0 : 1)}K`
          : min.toLocaleString()
      }–${
        max / 1000 >= 1
          ? `${(max / 1000).toFixed(max % 1000 === 0 ? 0 : 1)}K`
          : max.toLocaleString()
      }`;
    const v = max ?? min ?? 0;
    return v >= 1000 ? `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K` : `$${v.toLocaleString()}`;
  };

  return (
    <div
      ref={cardWrapRef}
      // no width cap: the list takes the frame's full width like every other page (a 27" used to show it as a
      // centred 1200px column)
      style={{
        paddingTop: 0,
        paddingBottom: m ? 10 : 0,
        paddingLeft: m ? 10 : 0,
        paddingRight: m ? 10 : 0,
        fontFamily: "'Manrope',sans-serif",
      }}>
      <div
        id="inspection-punch-list-card"
        style={{
          ...card(),
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          // a computer: the window less the frame around it (20px gutter, the 60px property header, 20px, and the 20px
          // bottom gutter), so the card ends on the same line as the sidebar
          height: m ? mobileCardHeight : 'calc(100dvh - 120px)',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '20px 20px 12px',
            flexShrink: 0,
          }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 17,
                fontWeight: 400,
                letterSpacing: '-0.015em',
                color: C.text,
                fontFamily: "'Manrope',sans-serif",
              }}>
              Prepare bid request
            </div>
          </div>
          <RFQCompileIndicator />
          {/* the page is a step off Repair Planner, so it closes like Settings: the ds X, back to where you came from */}
          <CloseButton
            label="Close bid request"
            onClose={() =>
              window.history.length > 1 ? navigate(-1) : navigate('/dashboard/repair-planner')
            }
          />
        </div>

        {/* Toolbar: search + filters + bulk actions */}
        <div
          style={{
            padding: '0 20px 12px',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}>
          {/* Row 1: Search (+ Status on desktop) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div
              style={{
                flex: 1,
                // Mobile: shrink search so sort/filter/expand buttons stay
                // on the same row as the search input.
                minWidth: m ? 0 : 180,
                height: 40,
                background: 'var(--tx-fill)',
                border: 0,
                borderRadius: '12px',
                padding: '0 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxSizing: 'border-box',
              }}>
              <Search size={16} color={C.muted} strokeWidth={2} style={{ flexShrink: 0 }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  fontSize: 12,
                  color: C.text,
                  fontFamily: "'Manrope',sans-serif",
                  flex: 1,
                }}
              />
            </div>

            <Stack direction="row" alignItems="center" spacing={'8px'}>
              {m ? (
                <IconButton
                  onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                  size="small"
                  aria-label="Sort"
                  sx={{
                    bgcolor: 'var(--tx-fill)',
                    border: 0,
                    color: 'var(--tx-muted)',
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    transition: 'color 0.15s, background 0.15s',
                    '&:hover': { bgcolor: 'var(--tx-fill-2)', color: C.text },
                  }}>
                  <ArrowUpDown size={16} />
                </IconButton>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                  startIcon={<ArrowUpDown size={14} />}
                  sx={{
                    bgcolor: 'var(--tx-fill)',
                    border: 0,
                    color: 'var(--tx-muted)',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    height: 40,
                    borderRadius: '8px',
                    px: '20px',
                    boxShadow: 'none',
                    transition: 'color 0.15s, background 0.15s',
                    '&:hover': {
                      bgcolor: 'var(--tx-fill-2)',
                      color: C.text,
                      boxShadow: 'none',
                    },
                  }}>
                  Sort
                </Button>
              )}
              {m ? (
                <IconButton
                  onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                  size="small"
                  aria-label="Filter"
                  sx={{
                    bgcolor: 'var(--tx-fill)',
                    border: 0,
                    color: 'var(--tx-muted)',
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    transition: 'color 0.15s, background 0.15s',
                    '&:hover': { bgcolor: 'var(--tx-fill-2)', color: C.text },
                  }}>
                  <ListFilter size={16} />
                </IconButton>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                  startIcon={<ListFilter size={14} />}
                  sx={{
                    bgcolor: 'var(--tx-fill)',
                    border: 0,
                    color: 'var(--tx-muted)',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    height: 40,
                    borderRadius: '8px',
                    px: '20px',
                    boxShadow: 'none',
                    transition: 'color 0.15s, background 0.15s',
                    '&:hover': {
                      bgcolor: 'var(--tx-fill-2)',
                      color: C.text,
                      boxShadow: 'none',
                    },
                  }}>
                  Filter
                </Button>
              )}
              {(() => {
                const allExpanded = filtered.length > 0 && expandedIds.size === filtered.length;
                return (
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedIds(allExpanded ? new Set() : new Set(filtered.map((i) => i.id)))
                    }
                    title={allExpanded ? 'Collapse all' : 'Expand all'}
                    aria-label={allExpanded ? 'Collapse all' : 'Expand all'}
                    style={{
                      background: 'var(--tx-fill)',
                      border: 0,
                      borderRadius: 8,
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: 'var(--tx-muted)',
                      transition: 'color 0.15s, background 0.15s',
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = C.text;
                      (e.currentTarget as HTMLButtonElement).style.background = 'var(--tx-fill-2)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-muted)';
                      (e.currentTarget as HTMLButtonElement).style.background = 'var(--tx-fill)';
                    }}>
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      {allExpanded ? (
                        <>
                          <polyline points="4 14 10 14 10 20" />
                          <polyline points="20 10 14 10 14 4" />
                          <line x1="10" y1="14" x2="3" y2="21" />
                          <line x1="21" y1="3" x2="14" y2="10" />
                        </>
                      ) : (
                        <>
                          <polyline points="15 3 21 3 21 9" />
                          <polyline points="9 21 3 21 3 15" />
                          <line x1="21" y1="3" x2="14" y2="10" />
                          <line x1="3" y1="21" x2="10" y2="14" />
                        </>
                      )}
                    </svg>
                  </button>
                );
              })()}
            </Stack>
          </div>

          {/* Filters row */}
          <div
            style={{
              display: m ? 'grid' : 'flex',
              gridTemplateColumns: m ? '1fr 1fr' : undefined,
              gap: m ? 8 : 16,
              flexWrap: m ? undefined : 'wrap',
              alignItems: 'center',
            }}>
            {m ? (
              <div style={{ position: 'relative', width: '100%' }}>
                <button
                  onClick={() => setUrgencyMenuOpen(!urgencyMenuOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 6,
                    padding: '5px 12px',
                    borderRadius: 6,
                    background: urgencyFilter.size > 0 ? `${C.canvas}2a` : 'var(--tx-fill)',
                    border: 0,
                    color: urgencyFilter.size > 0 ? C.text : 'var(--tx-muted)',
                    fontSize: 10,
                    fontWeight: 600,
                    fontFamily: "'Manrope',sans-serif",
                    cursor: 'pointer',
                    width: '100%',
                  }}>
                  {urgencyFilter.size === 0 ? 'Urgency' : `${urgencyFilter.size} urgency`}
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      transform: urgencyMenuOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform .15s',
                    }}>
                    <ChevronDown size={14} />
                  </span>
                </button>

                {urgencyMenuOpen && (
                  <>
                    <div
                      onClick={() => setUrgencyMenuOpen(false)}
                      style={{ position: 'fixed', inset: 0, zIndex: 100 }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        zIndex: 101,
                        minWidth: 200,
                        maxWidth: 'calc(100vw - 40px)',
                        maxHeight: 320,
                        overflowY: 'auto',
                        background: 'var(--tx-glass)',
                        backdropFilter: 'var(--tx-blur)',
                        WebkitBackdropFilter: 'var(--tx-blur)',
                        borderRadius: '12px',
                        boxShadow: 'var(--tx-shadow-sm)',
                        padding: 6,
                      }}>
                      {urgencyFilter.size > 0 && (
                        <button
                          onClick={() => setUrgencyFilter(new Set())}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            padding: '6px 10px',
                            fontSize: 10,
                            fontWeight: 600,
                            color: 'var(--tx-muted)',
                            cursor: 'pointer',
                            fontFamily: "'Manrope',sans-serif",
                            borderBottom: `1px solid ${C.border}`,
                            marginBottom: 4,
                          }}>
                          Clear all
                        </button>
                      )}
                      {(['Critical', 'Urgent', 'Moderate', 'Low', 'Monitor'] as const).map((u) => {
                        const active = urgencyFilter.has(u);
                        const col = ISSUES_SEV_COLORS[u];
                        return (
                          <div
                            key={u}
                            onClick={() => toggleUrgency(u)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '7px 10px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              background: active ? `${col}15` : 'transparent',
                              transition: 'background .15s',
                            }}>
                            <Checkbox checked={active} onChange={() => toggleUrgency(u)} />
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <span
                                style={{ width: 8, height: 8, borderRadius: 2, background: col }}
                              />
                              <span
                                style={{
                                  fontSize: 11,
                                  color: C.text,
                                  fontFamily: "'Manrope',sans-serif",
                                }}>
                                {u}: <span style={{ fontWeight: 600 }}>{urgencyCounts[u]}</span>
                              </span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 280,
                }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: C.muted,
                    fontFamily: "'Manrope',sans-serif",
                    marginRight: 4,
                  }}>
                  Urgency
                </span>
                {(['Critical', 'Urgent', 'Moderate', 'Low', 'Monitor'] as const).map((u) => {
                  const active = urgencyFilter.has(u);
                  const col = ISSUES_SEV_COLORS[u];
                  return (
                    <button
                      key={u}
                      onClick={() => toggleUrgency(u)}
                      aria-pressed={active}
                      style={{
                        // the app's legend item (a 2px tick in the urgency colour, the label, its count in ink); a
                        // selected filter takes the tonal tile instead of a coloured pill
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        height: 26,
                        padding: '0 8px',
                        borderRadius: 7,
                        border: 'none',
                        background: active ? 'var(--tx-sel-bg)' : 'transparent',
                        boxShadow: active ? 'var(--tx-sel-shadow)' : 'none',
                        color: active ? 'var(--tx-text)' : 'var(--tx-muted)',
                        fontSize: 11,
                        fontWeight: 500,
                        fontFamily: "'Manrope',sans-serif",
                        cursor: 'pointer',
                        transition: 'background 0.15s, color 0.15s',
                      }}>
                      <span style={{ width: 2, height: 12, borderRadius: 1, background: col }} />
                      {u}
                      <span style={{ color: 'var(--tx-text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {urgencyCounts[u]}
                      </span>
                    </button>
                  );
                })}
                {urgencyFilter.size > 0 && (
                  <button
                    onClick={() => setUrgencyFilter(new Set())}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: 10,
                      color: 'var(--tx-muted)',
                      cursor: 'pointer',
                      fontFamily: "'Manrope',sans-serif",
                      marginLeft: 4,
                    }}>
                    Clear
                  </button>
                )}
              </div>
            )}

            {allCategories.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                  position: 'relative',
                  width: m ? '100%' : 'auto',
                }}>
                {!m && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: C.muted,
                      fontFamily: "'Manrope',sans-serif",
                    }}>
                    Category
                  </span>
                )}
                <button
                  onClick={() => setCategoryMenuOpen(!categoryMenuOpen)}
                  style={{
                    display: m ? 'flex' : 'inline-flex',
                    alignItems: 'center',
                    justifyContent: m ? 'space-between' : 'flex-start',
                    gap: 6,
                    // tonal like the urgency filters: a fill, no outline; the selected tile once a category is picked
                    height: 26,
                    padding: '0 10px',
                    borderRadius: 7,
                    background: categoryFilter.size > 0 ? 'var(--tx-sel-bg)' : 'var(--tx-fill)',
                    boxShadow: categoryFilter.size > 0 ? 'var(--tx-sel-shadow)' : 'none',
                    border: 'none',
                    color: categoryFilter.size > 0 ? C.text : 'var(--tx-muted)',
                    fontSize: 11,
                    fontWeight: 500,
                    fontFamily: "'Manrope',sans-serif",
                    cursor: 'pointer',
                    width: m ? '100%' : 'auto',
                  }}>
                  {m
                    ? categoryFilter.size === 0
                      ? 'Category'
                      : `${categoryFilter.size} category`
                    : categoryFilter.size === 0
                    ? 'All'
                    : `${categoryFilter.size} selected`}
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      transform: categoryMenuOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform .15s',
                    }}>
                    <ChevronDown size={14} />
                  </span>
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPieOpen(true);
                  }}
                  title="View breakdown by category"
                  aria-label="View breakdown by category"
                  className="pie-btn-pulse"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'var(--tx-fill)',
                    border: 0,
                    color: 'var(--tx-muted)',
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'color 0.15s, background 0.15s',
                    fontFamily: "'Manrope',sans-serif",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color = C.text;
                    (e.currentTarget as HTMLButtonElement).style.background = 'var(--tx-fill-2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-muted)';
                    (e.currentTarget as HTMLButtonElement).style.background = 'var(--tx-fill)';
                  }}>
                  <style>{`
                    .pie-btn-pulse-wrap { position: relative; width: 14px; height: 14px; }
                    .pie-btn-pulse-wrap svg.lucide { display: block; }
                    .pie-btn-pulse-arc { position: absolute; inset: 0; pointer-events: none; }
                    .pie-btn-pulse-arc path { animation: pieRingPong 1.4s ease-in-out infinite alternate; }
                    .pie-btn-pulse-wrap svg.lucide path:nth-of-type(1) { animation: pieWedgeFill 2.8s ease-in-out infinite; }
                    @keyframes pieRingPong { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -42; } }
                    @keyframes pieWedgeFill { 0%, 100% { fill: rgba(192, 255, 2, 0); } 50% { fill: rgba(192, 255, 2, 1); } }
                    @media (prefers-reduced-motion: reduce) {
                      .pie-btn-pulse-arc path, .pie-btn-pulse-wrap svg.lucide path:nth-of-type(1) { animation: none !important; }
                    }
                  `}</style>
                  <span className="pie-btn-pulse-wrap">
                    <PieChartIcon size={14} strokeWidth={2} className="lucide" />
                    <svg
                      className="pie-btn-pulse-arc"
                      width={14}
                      height={14}
                      viewBox="0 0 24 24"
                      fill="none">
                      <path
                        d="M21.21 15.89A10 10 0 1 1 8 2.83"
                        stroke={C.lime}
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        strokeDasharray="5 50"
                        style={{ filter: `drop-shadow(0 0 3px ${C.lime})` }}
                      />
                    </svg>
                  </span>
                </button>

                {categoryMenuOpen && (
                  <>
                    <div
                      onClick={() => setCategoryMenuOpen(false)}
                      style={{ position: 'fixed', inset: 0, zIndex: 100 }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        right: 0,
                        zIndex: 101,
                        minWidth: m ? 200 : 240,
                        maxWidth: 'calc(100vw - 40px)',
                        maxHeight: 320,
                        overflowY: 'auto',
                        background: 'var(--tx-glass)',
                        backdropFilter: 'var(--tx-blur)',
                        WebkitBackdropFilter: 'var(--tx-blur)',
                        borderRadius: '12px',
                        boxShadow: 'var(--tx-shadow-sm)',
                        padding: 6,
                      }}>
                      {categoryFilter.size > 0 && (
                        <button
                          onClick={() => setCategoryFilter(new Set())}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            padding: '6px 10px',
                            fontSize: 10,
                            fontWeight: 600,
                            color: 'var(--tx-muted)',
                            cursor: 'pointer',
                            fontFamily: "'Manrope',sans-serif",
                            borderBottom: `1px solid ${C.border}`,
                            marginBottom: 4,
                          }}>
                          Clear all
                        </button>
                      )}
                      {allCategories.map((cat) => {
                        const active = categoryFilter.has(cat);
                        return (
                          <div
                            key={cat}
                            onClick={() => toggleCategory(cat)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '7px 10px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              background: active ? `${C.lime}10` : 'transparent',
                              transition: 'background .15s',
                            }}
                            onMouseEnter={(e) => {
                              if (!active) e.currentTarget.style.background = C.bg4;
                            }}
                            onMouseLeave={(e) => {
                              if (!active) e.currentTarget.style.background = 'transparent';
                            }}>
                            <Checkbox checked={active} onChange={() => toggleCategory(cat)} />
                            <span
                              style={{
                                fontSize: 11,
                                color: C.text,
                                fontFamily: "'Manrope',sans-serif",
                              }}>
                              {cat}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Bulk action bar — always rendered (anchored) so the Create RFQ
              CTA is discoverable before the user has selected anything.
              Buttons disable when nothing is selected. */}
          {(() => {
            const hasSelection = selectedIds.size > 0;
            const isCompile = selectedIds.size >= 2;
            const isBusy = Boolean(shareProgress);
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: m ? 8 : 12,
                  padding: '4px 0',
                }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: hasSelection ? 'var(--tx-text)' : 'var(--tx-muted)',
                    fontFamily: "'Manrope',sans-serif",
                    whiteSpace: 'nowrap',
                  }}>
                  {shareProgress
                    ? `Generating ${shareProgress.done}/${shareProgress.total} RFQs…`
                    : hasSelection
                    ? `${selectedIds.size} selected${
                        m ? '' : ` · ~$${Math.round(selectedCost).toLocaleString()} total`
                      }`
                    : m
                    ? 'Select items'
                    : 'Select items to create or compile an RFQ'}
                </span>
                <div style={{ flex: 1 }} />
                <button
                  onClick={() => setRfqHistoryOpen(true)}
                  style={{
                    // tonal (ds secondary): a fill, no stroke
                    height: 32,
                    background: 'var(--tx-fill)',
                    border: 'none',
                    borderRadius: 9,
                    padding: m ? '0 10px' : '0 14px',
                    fontSize: m ? 11 : 12,
                    fontWeight: 600,
                    color: 'var(--tx-text)',
                    cursor: 'pointer',
                    fontFamily: "'Manrope',sans-serif",
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                  <Clock size={m ? 12 : 13} />
                  History
                </button>
                <button
                  onClick={handleShareRFQs}
                  disabled={!hasSelection || isBusy}
                  style={{
                    // the page's one action: lime primary once rows are picked, a quiet tonal button until then
                    height: 32,
                    background: hasSelection ? 'var(--tx-lime)' : 'var(--tx-fill)',
                    border: 'none',
                    borderRadius: 9,
                    padding: m ? '0 12px' : '0 16px',
                    fontSize: m ? 11 : 12,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: hasSelection ? '#262626' : 'var(--tx-muted)',
                    cursor: !hasSelection ? 'not-allowed' : isBusy ? 'wait' : 'pointer',
                    fontFamily: "'Manrope',sans-serif",
                    whiteSpace: 'nowrap',
                    opacity: !hasSelection ? 0.45 : isBusy ? 0.6 : 1,
                  }}>
                  {isCompile ? 'Compile RFQ' : 'Create RFQ'}
                </button>
                {hasSelection && (
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    style={{
                      height: 32,
                      background: 'var(--tx-fill)',
                      border: 'none',
                      borderRadius: 9,
                      padding: m ? '0 10px' : '0 14px',
                      fontSize: m ? 11 : 12,
                      fontWeight: 600,
                      color: 'var(--tx-text)',
                      cursor: 'pointer',
                      fontFamily: "'Manrope',sans-serif",
                      whiteSpace: 'nowrap',
                    }}>
                    Clear
                  </button>
                )}
              </div>
            );
          })()}
        </div>

        {/* Table header */}
        {m ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 20px',
              borderTop: `1px solid ${C.border}`,
              borderBottom: `1px solid ${C.border}`,
              flexShrink: 0,
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              boxShadow: 'var(--tx-shadow)',
            }}>
            <Checkbox
              checked={filtered.length > 0 && selectedIds.size === filtered.length}
              onChange={toggleSelectAll}
            />
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: C.muted,
                fontFamily: "'Manrope',sans-serif",
              }}>
              Select all
            </span>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
              title={`Sort ${sortDir === 'asc' ? 'ascending' : 'descending'} by urgency`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: "'Manrope',sans-serif",
              }}>
              <span
                style={{
                  fontSize: 10,
                  color: C.muted,
                  fontWeight: 600,
                }}>
                Sort
              </span>
              <SortCaret active dir={sortDir} />
            </button>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '36px 24px 1fr 140px 120px 96px 80px',
              gap: 0,
              padding: '8px 20px',
              borderTop: `1px solid ${C.border}`,
              borderBottom: `1px solid ${C.border}`,
              flexShrink: 0,
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              boxShadow: 'var(--tx-shadow)',
            }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={filtered.length > 0 && selectedIds.size === filtered.length}
                onChange={toggleSelectAll}
              />
            </div>
            <div />
            <HeaderCell
              label="Item"
              active={sortKey === 'name'}
              dir={sortDir}
              onClick={() => toggleSort('name')}
            />
            <HeaderCell
              label="Category"
              active={sortKey === 'category'}
              dir={sortDir}
              onClick={() => toggleSort('category')}
              marginLeft={-20}
            />
            <HeaderCell
              label="Urgency"
              active={sortKey === 'urgency'}
              dir={sortDir}
              onClick={() => toggleSort('urgency')}
              marginLeft={20}
            />
            <HeaderCell
              label="Est. cost"
              active={sortKey === 'cost'}
              dir={sortDir}
              onClick={() => toggleSort('cost')}
              align="right"
            />
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: C.muted,
                fontFamily: "'Manrope',sans-serif",
                textAlign: 'center',
              }}>
              Status
            </div>
          </div>
        )}

        {/* Rows */}
        <style>{`.ipl-scroll::-webkit-scrollbar{width:0;background:transparent}.ipl-scroll{scrollbar-width:none}`}</style>
        <ScrollHintList className="ipl-scroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {filtered.length === 0 ? (
            <div
              style={{
                padding: 40,
                textAlign: 'center',
                color: C.muted,
                fontSize: 13,
                fontFamily: "'Manrope',sans-serif",
              }}>
              {!selectedProperty
                ? 'Select a property to view your punch list'
                : 'No items match your filters'}
            </div>
          ) : (
            filtered.map((issue, idx) => {
              const col = ISSUES_SEV_COLORS[issue.urgency] ?? C.muted;
              const isSel = selectedIds.has(issue.id);
              const isExpanded = expandedIds.has(issue.id);
              const timelineLabel = fmtTimeline(issue);
              const detailBlock = isExpanded ? (
                <div
                  style={{
                    paddingTop: 8,
                    minWidth: 0,
                    fontFamily: "'Manrope',sans-serif",
                  }}>
                  {issue.description && (
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--tx-muted)',
                        lineHeight: 1.55,
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                      }}>
                      {issue.description}
                    </div>
                  )}
                  {/* Mobile: timeline lives here. Desktop puts it under cost. */}
                  {m && timelineLabel && (
                    <div
                      style={{
                        marginTop: issue.description ? 8 : 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 11,
                        color: C.text,
                        fontWeight: 600,
                      }}>
                      <Clock size={13} color={C.muted} />
                      {timelineLabel}
                    </div>
                  )}
                </div>
              ) : null;
              const costTimelineBlock =
                isExpanded && timelineLabel ? (
                  <div
                    style={{
                      paddingTop: 8,
                      display: 'inline-flex',
                      alignItems: 'baseline',
                      gap: 6,
                      fontSize: 12,
                      lineHeight: 1.55,
                      color: C.text,
                      fontFamily: "'Manrope',sans-serif",
                      fontWeight: 600,
                    }}>
                    <Clock size={14} color={C.muted} style={{ alignSelf: 'center' }} />
                    {timelineLabel}
                  </div>
                ) : null;

              const chevronBtn = (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(issue.id);
                  }}
                  aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                  aria-expanded={isExpanded}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    padding: 0,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: C.muted,
                    flexShrink: 0,
                  }}>
                  <ChevronRight
                    size={16}
                    style={{
                      transform: isExpanded ? 'rotate(90deg)' : 'none',
                      transition: 'transform .15s',
                    }}
                  />
                </button>
              );

              if (m) {
                return (
                  <div
                    key={issue.id}
                    style={{
                      borderBottom: idx < filtered.length - 1 ? `1px solid ${C.border}44` : 'none',
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        gap: 10,
                        padding: '12px 20px',
                        alignItems: 'flex-start',
                        background: isSel
                          ? `${C.lime}08`
                          : issue.resolved
                          ? '#1a1a1a'
                          : 'transparent',
                      }}>
                      <Checkbox checked={isSel} onChange={() => toggleSelect(issue.id)} />
                      <div style={{ marginTop: -2 }}>{chevronBtn}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 8,
                          }}>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: issue.resolved ? C.muted : C.text,
                              textDecoration: issue.resolved ? 'line-through' : 'none',
                              fontFamily: "'Manrope',sans-serif",
                              lineHeight: 1.3,
                              minWidth: 0,
                              flex: 1,
                            }}>
                            {issue.title}
                          </span>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: C.text,
                              fontFamily: "'Manrope',sans-serif",
                              flexShrink: 0,
                            }}>
                            {fmtCost(issue)}
                          </span>
                        </div>
                        {detailBlock}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginTop: 6,
                            flexWrap: 'wrap',
                          }}>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 500,
                              color: 'var(--tx-text)',
                              fontFamily: "'Manrope',sans-serif",
                            }}>
                            {issue.urgency}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              color: 'var(--tx-muted)',
                              fontFamily: "'Manrope',sans-serif",
                            }}>
                            {issue.trade ?? issue.category}
                          </span>
                          <div style={{ flex: 1 }} />
                          <span
                            style={{
                              background: issue.resolved ? 'rgba(192,255,2,0.12)' : 'rgba(var(--tx-ink-rgb), 0.06)',
                              border: 'none',
                              color: issue.resolved ? 'var(--tx-lime-ink)' : C.muted,
                              borderRadius: 6,
                              padding: '3px 8px',
                              fontSize: 10,
                              fontWeight: 600,
                              fontFamily: "'Manrope',sans-serif",
                            }}>
                            {issue.resolved ? '✓ Done' : 'Open'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={issue.id}
                  onClick={() => toggleExpand(issue.id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '36px 24px 1fr 140px 120px 96px 80px',
                    gap: 0,
                    padding: '10px 20px',
                    borderBottom: idx < filtered.length - 1 ? `1px solid ${C.border}44` : 'none',
                    alignItems: 'start',
                    background: isSel ? `${C.lime}08` : issue.resolved ? '#1a1a1a' : 'transparent',
                    transition: 'background .15s',
                    cursor: 'pointer',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', height: 24 }}>
                    <Checkbox checked={isSel} onChange={() => toggleSelect(issue.id)} />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      height: 24,
                    }}>
                    {chevronBtn}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0,
                      minWidth: 0,
                      paddingRight: 80,
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        minWidth: 0,
                        height: 24,
                      }}>
                      <div
                        style={{
                          width: 3,
                          height: 20,
                          borderRadius: 2,
                          background: col,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: issue.resolved ? C.muted : C.text,
                          textDecoration: issue.resolved ? 'line-through' : 'none',
                          fontFamily: "'Manrope',sans-serif",
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minWidth: 0,
                          flex: 1,
                        }}>
                        {issue.title}
                      </span>
                    </div>
                    {detailBlock}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: 24,
                      marginLeft: -20,
                      fontSize: 11,
                      color: 'var(--tx-muted)',
                      fontFamily: "'Manrope',sans-serif",
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                    {issue.trade ?? issue.category}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: 24,
                      marginLeft: 20,
                    }}>
                    {/* plain text: the row's own tick already carries the urgency colour */}
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: 'var(--tx-muted)',
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      {issue.urgency}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      fontFamily: "'Manrope',sans-serif",
                    }}>
                    <div
                      style={{
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: 12,
                        fontWeight: 600,
                        color: C.text,
                      }}>
                      {fmtCost(issue)}
                    </div>
                    {costTimelineBlock}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 24,
                    }}>
                    <span
                      style={{
                        // the app's square tonal tag: a fill, no outline
                        background: issue.resolved ? 'rgba(192,255,2,0.12)' : 'rgba(var(--tx-ink-rgb), 0.06)',
                        border: 'none',
                        color: issue.resolved ? 'var(--tx-lime-ink)' : C.muted,
                        borderRadius: 6,
                        padding: '3px 8px',
                        fontSize: 10,
                        fontWeight: 600,
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      {issue.resolved ? '✓ Done' : 'Open'}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </ScrollHintList>

        {/* Footer count */}
        <div
          style={{
            padding: m ? '10px 20px 20px' : '12px 20px 20px',
            borderTop: `1px solid ${C.border}`,
            fontSize: 11,
            color: C.muted,
            fontFamily: "'Manrope',sans-serif",
            flexShrink: 0,
          }}>
          Showing {filtered.length} of {allIssues.length}{' '}
          {allIssues.length === 1 ? 'item' : 'items'}
        </div>

        {m && selectedProperty && (
          <RfqHistoryDialog
            open={rfqHistoryOpen}
            propertyId={selectedProperty.property.id}
            issues={allIssues}
            containedMobile
            onClose={() => setRfqHistoryOpen(false)}
            onOpenRFQ={(rfq) => {
              const linkedIssue = rfq.issueId
                ? allIssues.find((i) => i.id === rfq.issueId)
                : undefined;
              const trade = linkedIssue?.trade ?? linkedIssue?.componentCategory ?? linkedIssue?.category;
              setReviewSuggestedTrades(trade ? [trade] : []);
              setReviewRFQ(rfq);
            }}
          />
        )}
      </div>

      {/* Sort menu */}
      <Menu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={() => setSortMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              borderRadius: '16px',
              minWidth: 200,
              mt: 1,
              pt: '20px',
              px: '20px',
              pb: '14px',
              boxShadow: 'var(--tx-shadow)',
            },
          },
        }}
        sx={{
          '& .MuiMenuItem-root': { borderBottom: 'none' },
          '& .MuiList-root': { p: 0 },
        }}>
        <Typography
          variant="caption"
          sx={{
            color: 'var(--tx-muted)',
            px: '12px',
            pb: '20px',
            display: 'block',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.3em',
          }}>
          SORT
        </Typography>
        {(Object.keys(SORT_LABELS) as IssuesSortKey[]).map((key) => (
          <MenuItem
            key={key}
            onClick={() => {
              setSortKey(key);
              setSortMenuAnchor(null);
            }}
            selected={sortKey === key}
            sx={{
              color: sortKey === key ? 'var(--tx-text)' : 'var(--tx-muted)',
              bgcolor: sortKey === key ? 'var(--tx-fill-2)' : 'transparent',
              fontSize: 14,
              fontWeight: sortKey === key ? 600 : 400,
              borderRadius: '8px',
              mx: 0,
              paddingTop: '6px !important',
              paddingBottom: '6px !important',
              paddingLeft: '12px !important',
              paddingRight: '12px !important',
              lineHeight: '1.4 !important',
              '&.Mui-selected': {
                bgcolor: 'var(--tx-fill-2)',
                color: 'var(--tx-text)',
                '&:hover': { bgcolor: '#BBB7AF' },
              },
              '&:hover': {
                bgcolor: sortKey === key ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
              },
            }}>
            {SORT_LABELS[key]}
          </MenuItem>
        ))}
      </Menu>

      {/* Filter menu (categories) */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              borderRadius: '16px',
              minWidth: 220,
              maxHeight: 360,
              mt: 1,
              pt: '20px',
              px: '20px',
              pb: '14px',
              boxShadow: 'var(--tx-shadow)',
            },
          },
        }}
        sx={{
          '& .MuiMenuItem-root': { borderBottom: 'none' },
          '& .MuiList-root': { p: 0 },
        }}>
        <Typography
          variant="caption"
          sx={{
            color: 'var(--tx-muted)',
            px: '12px',
            pb: '20px',
            display: 'block',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.3em',
          }}>
          BY CATEGORY
        </Typography>
        <MenuItem
          onClick={() => setCategoryFilter(new Set())}
          selected={categoryFilter.size === 0}
          sx={{
            color: categoryFilter.size === 0 ? 'var(--tx-text)' : 'var(--tx-muted)',
            bgcolor: categoryFilter.size === 0 ? 'var(--tx-fill-2)' : 'transparent',
            fontSize: 14,
            fontWeight: categoryFilter.size === 0 ? 600 : 400,
            borderRadius: '8px',
            paddingTop: '6px !important',
            paddingBottom: '6px !important',
            paddingLeft: '12px !important',
            paddingRight: '12px !important',
            lineHeight: '1.4 !important',
            '&.Mui-selected': {
              bgcolor: 'var(--tx-fill-2)',
              color: 'var(--tx-text)',
              '&:hover': { bgcolor: '#BBB7AF' },
            },
            '&:hover': {
              bgcolor: categoryFilter.size === 0 ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
            },
          }}>
          All categories
        </MenuItem>
        {allCategories.map((cat) => {
          const active = categoryFilter.has(cat);
          return (
            <MenuItem
              key={cat}
              onClick={() => toggleCategory(cat)}
              selected={active}
              sx={{
                color: active ? 'var(--tx-text)' : 'var(--tx-muted)',
                bgcolor: active ? 'var(--tx-fill-2)' : 'transparent',
                fontSize: 14,
                fontWeight: active ? 600 : 400,
                borderRadius: '8px',
                paddingTop: '6px !important',
                paddingBottom: '6px !important',
                paddingLeft: '12px !important',
                paddingRight: '12px !important',
                lineHeight: '1.4 !important',
                '&.Mui-selected': {
                  bgcolor: 'var(--tx-fill-2)',
                  color: 'var(--tx-text)',
                  '&:hover': { bgcolor: '#BBB7AF' },
                },
                '&:hover': {
                  bgcolor: active ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
                },
              }}>
              {cat}
            </MenuItem>
          );
        })}
      </Menu>

      {pieOpen &&
        pieBlurLeft !== null &&
        pieContentRect &&
        createPortal(
          <>
            <Box
              onClick={() => setPieOpen(false)}
              sx={{
                position: 'fixed',
                zIndex: 1100,
                backgroundColor: m ? 'var(--tx-glass-panel)' : 'var(--tx-glass-panel)',
                backdropFilter: m ? 'blur(14px) saturate(160%)' : 'none',
                WebkitBackdropFilter: m ? 'blur(14px) saturate(160%)' : 'none',
                ...(pieCardRect
                  ? {
                      top: pieCardRect.top,
                      left: pieCardRect.left,
                      width: pieCardRect.width,
                      height: pieCardRect.height,
                      borderRadius: '25px',
                    }
                  : {
                      top: 0,
                      bottom: 0,
                      left: pieBlurLeft,
                      right: 0,
                    }),
              }}
            />
            <Box
              onClick={!m ? () => setPieOpen(false) : undefined}
              sx={{
                position: 'fixed',
                zIndex: 1101,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: m ? 'none' : 'auto',
                cursor: !m ? 'pointer' : 'default',
                ...(pieCardRect
                  ? {
                      top: pieCardRect.top,
                      left: pieCardRect.left,
                      width: pieCardRect.width,
                      height: pieCardRect.height,
                    }
                  : {
                      top: pieContentRect.top,
                      left: pieContentRect.left,
                      width: pieContentRect.width,
                      bottom: 0,
                    }),
              }}>
              {m && (
                <CloseButton
                  onClose={() => setPieOpen(false)}
                  label="Close"
                  sx={{ position: 'absolute', top: 18, right: 18, pointerEvents: 'auto' }}
                />
              )}
              {(() => {
                // cost by category as a breakdown, not a poster: a thin ring beside its legend. Slices run largest
                // first; past the top seven the rest fold into "Other" so the ring never shatters into slivers
                const fmtK = (v: number) =>
                  v >= 1_000_000
                    ? `$${(v / 1_000_000).toFixed(2)}M`
                    : v >= 1000
                    ? `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K`
                    : `$${Math.round(v).toLocaleString()}`;
                const ranked = categoryBreakdown.filter((c) => c.totalCost > 0).sort((a, b) => b.totalCost - a.totalCost);
                const TOP = 7;
                const rest = ranked.slice(TOP);
                const slices =
                  rest.length > 1
                    ? [
                        ...ranked.slice(0, TOP),
                        {
                          name: `Other · ${rest.length} trades`,
                          count: rest.reduce((n, c) => n + c.count, 0),
                          totalCost: rest.reduce((n, c) => n + c.totalCost, 0),
                          color: 'var(--tx-fill-3)',
                        },
                      ]
                    : ranked;
                const total = slices.reduce((n, c) => n + c.totalCost, 0);
                const head = (
                  <div
                    style={{
                      position: 'absolute',
                      top: 20,
                      left: 24,
                      right: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontFamily: "'Manrope',sans-serif",
                      pointerEvents: 'auto',
                    }}>
                    <span style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: 'var(--tx-text)' }}>
                      Repair cost by category
                    </span>
                    <span onClick={(e) => e.stopPropagation()}>
                      <CloseButton onClose={() => setPieOpen(false)} label="Close" size="sm" />
                    </span>
                  </div>
                );
                if (slices.length === 0) {
                  return (
                    <>
                      {head}
                      <Box sx={{ color: C.muted, fontFamily: "'Manrope',sans-serif", fontSize: 13, pointerEvents: 'auto' }}>
                        No items to break down.
                      </Box>
                    </>
                  );
                }
                const cardW = pieCardRect ? pieCardRect.width : 900;
                const cardH = pieCardRect ? pieCardRect.height : 720;
                const size = m
                  ? Math.min(cardW - 80, 260)
                  : Math.max(220, Math.min(cardH - 160, (cardW - 48) * 0.45, 460));
                const outer = Math.floor(size / 2);
                const inner = outer - Math.max(16, Math.round(size * 0.07));
                return (
                  <>
                    {head}
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        display: 'flex',
                        flexDirection: m ? 'column' : 'row',
                        alignItems: 'center',
                        gap: m ? '28px' : '64px',
                        pt: m ? '56px' : '40px',
                        px: m ? '20px' : '40px',
                        maxHeight: '100%',
                        overflowY: m ? 'auto' : 'visible',
                        pointerEvents: 'auto',
                        cursor: 'default',
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      <Box
                        sx={{
                          position: 'relative',
                          width: size,
                          height: size,
                          flexShrink: 0,
                          '& .recharts-wrapper, & .recharts-surface, & .recharts-sector, & path': {
                            outline: 'none !important',
                            WebkitTapHighlightColor: 'transparent',
                          },
                        }}>
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            textAlign: 'center',
                          }}>
                          <span
                            style={{
                              fontSize: m ? 32 : 44,
                              fontWeight: 200,
                              letterSpacing: '-0.05em',
                              lineHeight: 1,
                              color: 'var(--tx-text)',
                            }}>
                            {pieHoverIdx != null && slices[pieHoverIdx] ? fmtK(slices[pieHoverIdx].totalCost) : fmtK(total)}
                          </span>
                          <span style={{ marginTop: 8, fontSize: 12, fontWeight: 500, color: 'var(--tx-muted)', maxWidth: inner * 1.5 }}>
                            {pieHoverIdx != null && slices[pieHoverIdx] ? slices[pieHoverIdx].name : 'Estimated repairs'}
                          </span>
                        </div>
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={slices}
                              dataKey="totalCost"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              startAngle={90}
                              endAngle={-270}
                              innerRadius={inner}
                              outerRadius={outer}
                              paddingAngle={1.5}
                              cornerRadius={2}
                              isAnimationActive={false}
                              onMouseEnter={(_, idx) => setPieHoverIdx(idx)}
                              onMouseLeave={() => setPieHoverIdx(null)}>
                              {slices.map((entry, idx) => (
                                <Cell
                                  key={entry.name}
                                  fill={pieHoverIdx === idx ? C.lime : entry.color}
                                  fillOpacity={pieHoverIdx == null || pieHoverIdx === idx ? 1 : 0.45}
                                  stroke="none"
                                  style={{ transition: 'fill 0.15s, fill-opacity 0.15s' }}
                                />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      {/* the legend: tick, trade and its count, a share bar, the cost; hovering a row lights its slice */}
                      <div style={{ width: m ? '100%' : 380, display: 'flex', flexDirection: 'column' }}>
                        {slices.map((c, idx) => {
                          const share = total > 0 ? c.totalCost / total : 0;
                          const on = pieHoverIdx === idx;
                          return (
                            <div
                              key={c.name}
                              onMouseEnter={() => setPieHoverIdx(idx)}
                              onMouseLeave={() => setPieHoverIdx(null)}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '2px minmax(0, 1fr) 64px 64px',
                                alignItems: 'center',
                                columnGap: 12,
                                padding: '10px 0',
                                borderTop: idx === 0 ? 'none' : '1px solid var(--tx-line)',
                                opacity: pieHoverIdx == null || on ? 1 : 0.55,
                                transition: 'opacity 0.15s',
                              }}>
                              <span style={{ width: 2, height: 14, borderRadius: 1, background: on ? C.lime : c.color }} />
                              <span style={{ minWidth: 0 }}>
                                <span
                                  style={{
                                    display: 'block',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: 'var(--tx-text)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}>
                                  {c.name}
                                </span>
                                <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)' }}>
                                  {c.count} item{c.count === 1 ? '' : 's'} · {(share * 100).toFixed(share < 0.1 ? 1 : 0)}%
                                </span>
                              </span>
                              <span style={{ height: 2, borderRadius: 1, background: 'var(--tx-fill-2)', position: 'relative' }}>
                                <span
                                  style={{
                                    position: 'absolute',
                                    inset: '0 auto 0 0',
                                    width: `${Math.max(3, share * 100)}%`,
                                    borderRadius: 1,
                                    background: on ? C.lime : c.color,
                                  }}
                                />
                              </span>
                              <span
                                style={{
                                  textAlign: 'right',
                                  fontSize: 15,
                                  fontWeight: 300,
                                  letterSpacing: '-0.02em',
                                  color: 'var(--tx-text)',
                                  fontVariantNumeric: 'tabular-nums',
                                }}>
                                {fmtK(c.totalCost)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </Box>
                  </>
                );
              })()}
            </Box>
          </>,
          document.body,
        )}

      {reviewRFQ && selectedProperty && (
        <ReviewAndShareRFQDialog
          open={Boolean(reviewRFQ)}
          userProperty={selectedProperty}
          requestForQuotation={reviewRFQ}
          suggestedTrades={reviewSuggestedTrades}
          onClose={() => {
            setReviewRFQ(null);
            setReviewSuggestedTrades([]);
            clearRfqSearchParam();
          }}
        />
      )}
      <RfqGenerationDialog
        open={Boolean(shareProgress)}
        issues={rfqGenerationIssues}
        progress={shareProgress}
      />
      {!m && selectedProperty && (
        <RfqHistoryDialog
          open={rfqHistoryOpen}
          propertyId={selectedProperty.property.id}
          issues={allIssues}
          onClose={() => setRfqHistoryOpen(false)}
          onOpenRFQ={(rfq) => {
            // Categories are 1:1 with the vendor directory's trade taxonomy —
            // reuse the linked issue's trade directly rather than re-guessing
            // it from RFQ prose (which was silently discarding it via `[]`).
            const linkedIssue = rfq.issueId
              ? allIssues.find((i) => i.id === rfq.issueId)
              : undefined;
            const trade = linkedIssue?.trade ?? linkedIssue?.componentCategory ?? linkedIssue?.category;
            setReviewSuggestedTrades(trade ? [trade] : []);
            setReviewRFQ(rfq);
          }}
        />
      )}
      {reviewCompileRFQs && selectedProperty && (
        <ReviewAndShareCompileDialog
          open={Boolean(reviewCompileRFQs)}
          userProperty={selectedProperty}
          rfqs={reviewCompileRFQs}
          suggestedTrades={reviewSuggestedTrades}
          onClose={() => {
            setReviewCompileRFQs(null);
            setReviewSuggestedTrades([]);
          }}
        />
      )}
    </div>
  );
}
